import { useCallback, useEffect, useRef, useMemo } from 'react';

import { useForm, DefaultValues, Validate, Path } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

type ValidationConfig<
  T,
  U extends keyof T = keyof T,
  PathKey extends Extract<Path<T>, keyof T> = Extract<Path<T>, keyof T>
> = U extends PathKey
  ? {
      path: U;
      validate: (data: T[U]) => ReturnType<Validate<unknown, unknown>>;
    }
  : never;

type Config<T> = {
  validationConfigArray: Array<ValidationConfig<T>>;
  defaultValues?: T;
  fn?: (payload: T) => void;
  context?: Record<string, unknown>;
};
export const useGenericValidation = <T>(
  body: T,
  {
    validationConfigArray,
    defaultValues = undefined as unknown as T,
    fn,
  }: Config<T>
) => {
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues as DefaultValues<T>,
  });
  useEffect(() => {
    validationConfigArray.forEach(({ path, ...rest }) => {
      register(path, {
        validate: rest.validate as Validate<unknown, unknown>,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register]);
  return {
    onChange: <K extends Extract<Path<T>, keyof T>>(key: K, data: any) =>
      setValue(key, data),
    getValues,
    onsubmit: handleSubmit(fn ?? console.log),
    body,
    errors,
  };
};

type ConfigYup<T> = Omit<Config<T>, 'validationConfigArray'> & {
  validationPaths: Array<ValidationConfig<T>['path']>;
  schema: Parameters<typeof yupResolver>[0];
};
const byPassLoopWarningForUseEffect: typeof useEffect = useEffect;

export const useYupValidation = <T>(
  body: T,
  {
    validationPaths,
    defaultValues = undefined as unknown as T,
    schema,
    fn,
    context,
  }: ConfigYup<T>
) => {
  const methods = useForm({
    defaultValues: defaultValues as DefaultValues<T>,
    resolver: yupResolver(schema, { abortEarly: false }),
    context: context,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paths = useMemo(() => validationPaths, []);
  useEffect(() => {
    validationPaths.forEach((path) => {
      methods.register(path);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.register]);
  const {
    formState: { isSubmitted },
  } = methods;
  const { setValue } = methods;
  const onChange = useCallback(
    <K extends Extract<Path<T>, keyof T>>(key: K, data: any) => {
      setValue(key, data, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: isSubmitted,
      });
    },
    [setValue, isSubmitted]
  );
  const onInvalid = () => {
    return;
  };
  const mountedRef = useRef(false);
  paths.forEach((path) => {
    byPassLoopWarningForUseEffect(() => {
      mountedRef.current && onChange(path, body[path]);
    }, [JSON.stringify(body[path])]);
  });
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return {
    onChange,
    onSubmit: methods.handleSubmit(fn ?? onInvalid),
    body,
    methods,
  };
};
